import React, { useState, useEffect } from 'react';
import './Tour.css';

const tourSteps = [
  {
    target: '[data-tour="stats"]',
    title: 'Your Dashboard',
    message: 'View your total income, team size, direct referrals, and available balance at a glance.',
    position: 'bottom'
  },
  {
    target: '[data-tour="withdraw"]',
    title: 'Withdraw Earnings',
    message: 'Click here to withdraw all your available income directly to your wallet.',
    position: 'bottom'
  },
  {
    target: '[data-tour="packages"]',
    title: 'Time Units',
    message: 'Upgrade to higher packages to increase your earning potential. Each unit has a 3x return limit!',
    position: 'top'
  },
  {
    target: '[data-tour="team"]',
    title: 'Your Team',
    message: 'Track your team members across all 10 levels. See who joined under each package.',
    position: 'top'
  },
  {
    target: '[data-tour="telegram"]',
    title: 'Telegram Notifications',
    message: 'Link your Telegram to receive instant alerts for all income and team activities.',
    position: 'top'
  },
  {
    target: '[data-tour="activities"]',
    title: 'Activity History',
    message: 'View all your transactions, income received, and team activities in one place.',
    position: 'top'
  }
];

const Tour = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(updateTargetPosition, 300);
    window.addEventListener('resize', updateTargetPosition);
    window.addEventListener('scroll', updateTargetPosition);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateTargetPosition);
      window.removeEventListener('scroll', updateTargetPosition);
    };
  }, [currentStep]);

  const updateTargetPosition = () => {
    const step = tourSteps[currentStep];
    if (!step) return;
    
    const target = document.querySelector(step.target);
    if (target) {
      const rect = target.getBoundingClientRect();
      setTargetRect(rect);
      
      // Scroll target into view if needed
      if (rect.top < 80 || rect.bottom > window.innerHeight - 200) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => {
          const newRect = target.getBoundingClientRect();
          setTargetRect(newRect);
        }, 500);
      }
    } else {
      // If target not found, skip to next step
      if (currentStep < tourSteps.length - 1) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeTour();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    completeTour();
  };

  const completeTour = () => {
    setIsVisible(false);
    localStorage.setItem('timetotime_tour_completed', 'true');
    if (onComplete) onComplete();
  };

  if (!isVisible || !targetRect) return null;

  const step = tourSteps[currentStep];
  const isLastStep = currentStep === tourSteps.length - 1;
  const isFirstStep = currentStep === 0;

  return (
    <div className="tour-overlay">
      {/* Spotlight highlight */}
      <div 
        className="tour-highlight" 
        style={{
          top: `${targetRect.top - 8}px`,
          left: `${targetRect.left - 8}px`,
          width: `${targetRect.width + 16}px`,
          height: `${targetRect.height + 16}px`,
        }}
      />
      
      {/* Pulsing arrow */}
      <div 
        className={`tour-arrow ${step.position}`} 
        style={{
          top: step.position === 'bottom' 
            ? `${targetRect.bottom + 15}px` 
            : `${targetRect.top - 55}px`,
          left: `${targetRect.left + targetRect.width / 2}px`,
        }}
      >
        <div className="arrow-pulse"></div>
        <div className="arrow-icon">
          {step.position === 'bottom' ? '↑' : '↓'}
        </div>
      </div>

      {/* Fixed Bottom Tooltip */}
      <div className="tour-tooltip">
        <div className="tooltip-header">
          <span className="tooltip-step">{currentStep + 1}/{tourSteps.length}</span>
          <span className="tooltip-title">{step.title}</span>
        </div>
        <p className="tooltip-message">{step.message}</p>
        <div className="tooltip-actions">
          <button className="tour-skip" onClick={handleSkip}>
            Skip Tour
          </button>
          <div className="tour-nav-btns">
            {!isFirstStep && (
              <button className="tour-prev" onClick={handlePrev}>
                ← Back
              </button>
            )}
            <button className="tour-next" onClick={handleNext}>
              {isLastStep ? 'Get Started' : 'Next →'}
            </button>
          </div>
        </div>
        <div className="tooltip-dots">
          {tourSteps.map((_, idx) => (
            <span 
              key={idx} 
              className={`dot ${idx === currentStep ? 'active' : ''} ${idx < currentStep ? 'completed' : ''}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Tour;
